import { Injectable, inject, DestroyRef } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, take, filter, catchError, timeout, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import {
  PartnerPortalAccessStatus,
  PartnerPortalAccessStatusResponse,
  VerticalWorksAccessResponse,
  SITE_LOCATOR_ROLE,
} from '../models';
import { isVerticalWorksConfigured } from './external-redirect';

export interface AppAccess {
  hasAccess: boolean;
  roles: string[];
  userId?: string;
  // Left as a bare string deliberately: each app has its own status
  // vocabulary on the wire (Vertical Intel sends 'revoked', Vertical Works
  // sends the four VerticalWorksAccessStatus values), so no single enum
  // covers the field. The per-app values are typed where they are read.
  status?: string;
  // The check could not be made, as distinct from an answer of "no access".
  // Not part of any app's wire vocabulary, which is why it is a flag rather
  // than a `status` value: the app never sends it, the Portal infers it.
  //
  // A tile must not offer Request Access on an unanswered check. The button
  // posts to the same API that just failed, so the offer cannot be honoured,
  // and to a user with access it misreports their own account.
  statusUnknown?: boolean;
}

export interface AppRoles {
  colo: AppAccess;
  siteLocator: AppAccess;
  verticalIntel: AppAccess;
  dueDiligence: AppAccess;
  partnerPortal: AppAccess;
  verticalWorks: AppAccess;
}

const DEFAULT_APP_ROLES: AppRoles = {
  colo: { hasAccess: false, roles: [] },
  siteLocator: { hasAccess: false, roles: [] },
  verticalIntel: { hasAccess: false, roles: [] },
  dueDiligence: { hasAccess: false, roles: [] },
  partnerPortal: { hasAccess: false, roles: [] },
  verticalWorks: { hasAccess: false, roles: [] }
};

const APP_ROLES_CLAIM = 'https://verticalbridge.com/app_roles';
const LAST_REFRESH_KEY = 'appRolesLastRefresh';
const REFRESH_DEBOUNCE_MS = 5000; // Minimum 5 seconds between refreshes
// Cap each backend role check so one slow/unreachable API can't hold up the
// others. forkJoin waits for the slowest call, so without this an endpoint
// that hangs (e.g. a backend not yet deployed in an environment) would keep
// every tile disabled until the browser's own connection timeout fires.
const ROLE_CHECK_TIMEOUT_MS = 8000;

@Injectable({
  providedIn: 'root'
})
export class AppRolesService {
  private auth0 = inject(AuthService);
  private http = inject(HttpClient);
  private appRoles$ = new BehaviorSubject<AppRoles>(DEFAULT_APP_ROLES);
  private destroyRef = inject(DestroyRef);
  private isRolesLoaded = false;
  private _isRefreshing = false;
  private refreshPromise: Promise<AppRoles | null> | null = null;

  constructor() {
    this.initializeRoles();
  }

  private initializeRoles(): void {
    // First, try to load from stored token (direct login)
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      const roles = this.decodeRolesFromToken(storedToken);
      if (roles) {
        this.appRoles$.next(roles);
        this.isRolesLoaded = true;
        return;
      }
    }

    // Fall back to Auth0 SDK for social login
    this.auth0.idTokenClaims$.pipe(
      filter(claims => claims !== null && claims !== undefined),
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(claims => {
      if (this.isRolesLoaded) return;

      if (claims[APP_ROLES_CLAIM]) {
        const roles = this.withDefaults(claims[APP_ROLES_CLAIM]);
        this.appRoles$.next(roles);
        this.isRolesLoaded = true;
      } else {
        this.loadRolesFromAccessToken();
      }
    });
  }

  private loadRolesFromAccessToken(): void {
    if (this.isRolesLoaded) return;

    this.auth0.getAccessTokenSilently().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (token) => {
        const roles = this.decodeRolesFromToken(token);
        if (roles) {
          this.appRoles$.next(roles);
          this.isRolesLoaded = true;
        }
      },
      error: () => { /* token retrieval failed */ }
    });
  }

  /**
   * Fill in any app the incoming `app_roles` claim doesn't mention.
   *
   * The claim is minted by the Auth0 Action, which is pasted into each tenant
   * by hand and per environment — so a token in circulation can predate the
   * Action gaining an app. Vertical Works is the live case: tokens issued
   * before its branch is pasted carry the other four keys and nothing for it,
   * which without this merge leaves `roles.verticalWorks` undefined and
   * throws in the first template or redirect check that touches it.
   *
   * Merged per key rather than by spreading DEFAULT_APP_ROLES, for two
   * reasons. The claim is an unvalidated JWT payload, so a key present but
   * not an object (`verticalWorks: null`) would spread straight through and
   * throw on the first `.hasAccess` read — the exact crash this method
   * exists to prevent. And a shallow spread hands back the *same object
   * instance* as the module-level DEFAULT_APP_ROLES singleton for every
   * absent key, so one future in-place mutation would corrupt the default
   * for the rest of the page session and look like a caching bug.
   */
  private withDefaults(claim: Partial<AppRoles> | null | undefined): AppRoles {
    const merged = {} as AppRoles;
    for (const key of Object.keys(DEFAULT_APP_ROLES) as (keyof AppRoles)[]) {
      const incoming: unknown = claim?.[key];
      if (incoming && typeof incoming === 'object') {
        const access = incoming as Partial<AppAccess>;
        merged[key] = {
          hasAccess: !!access.hasAccess,
          roles: Array.isArray(access.roles) ? access.roles : [],
          userId: access.userId,
          status: access.status
        };
      } else {
        merged[key] = { hasAccess: false, roles: [] };
      }
    }
    return merged;
  }

  private decodeRolesFromToken(token: string): AppRoles | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const decoded = JSON.parse(atob(parts[1]));

      // Check for app_roles claim
      if (decoded[APP_ROLES_CLAIM]) {
        return this.withDefaults(decoded[APP_ROLES_CLAIM]);
      }

      // Check for roles array and convert to AppRoles format
      const roles: string[] = decoded['roles'] || decoded['https://verticalbridge.com/roles'] || [];
      if (roles.length > 0) {
        return this.convertRolesToAppRoles(roles);
      }

      return null;
    } catch {
      return null;
    }
  }

  private convertRolesToAppRoles(roles: string[]): AppRoles {
    // Convert a roles array to AppRoles format
    // Adjust these role names to match what Auth0 returns
    return {
      colo: {
        hasAccess: roles.some(r => r.toLowerCase().includes('colo') || r.toLowerCase().includes('online')),
        roles: roles.filter(r => r.toLowerCase().includes('colo') || r.toLowerCase().includes('online'))
      },
      siteLocator: {
        hasAccess: roles.some(r => r.toLowerCase().includes('sitelocator') || r.toLowerCase().includes('site')),
        roles: roles.filter(r => r.toLowerCase().includes('sitelocator') || r.toLowerCase().includes('site'))
      },
      verticalIntel: {
        hasAccess: roles.some(r => r.toLowerCase().includes('intel') || r.toLowerCase().includes('vertical')),
        roles: roles.filter(r => r.toLowerCase().includes('intel') || r.toLowerCase().includes('vertical'))
      },
      dueDiligence: {
        hasAccess: roles.some(r => r.toLowerCase().includes('diligence') || r.toLowerCase().includes('due')),
        roles: roles.filter(r => r.toLowerCase().includes('diligence') || r.toLowerCase().includes('due'))
      },
      partnerPortal: {
        hasAccess: roles.some(r => r.toLowerCase().includes('partner') || r.toLowerCase().includes('portal')),
        roles: roles.filter(r => r.toLowerCase().includes('partner') || r.toLowerCase().includes('portal'))
      },
      // Vertical Works authorizes from its own database rather than from the
      // roles array, so there is nothing to match on here — the legacy
      // roles-array path can only report "no access" for it.
      verticalWorks: { hasAccess: false, roles: [] }
    };
  }

  getRoles(): Observable<AppRoles> {
    return this.appRoles$.asObservable();
  }

  isRefreshing(): boolean {
    return this._isRefreshing;
  }

  getCurrentRoles(): AppRoles {
    return this.appRoles$.value;
  }

  hasColoAccess(): Observable<boolean> {
    return this.appRoles$.pipe(map(roles => roles.colo.hasAccess));
  }

  hasSiteLocatorAccess(): Observable<boolean> {
    return this.appRoles$.pipe(map(roles => roles.siteLocator.hasAccess));
  }

  hasVerticalIntelAccess(): Observable<boolean> {
    return this.appRoles$.pipe(map(roles => roles.verticalIntel.hasAccess));
  }

  hasDueDiligenceAccess(): Observable<boolean> {
    return this.appRoles$.pipe(map(roles => roles.dueDiligence.hasAccess));
  }

  hasPartnerPortalAccess(): Observable<boolean> {
    return this.appRoles$.pipe(map(roles => roles.partnerPortal.hasAccess));
  }

  hasVerticalWorksAccess(): Observable<boolean> {
    return this.appRoles$.pipe(map(roles => roles.verticalWorks.hasAccess));
  }

  getColoUrl(): string {
    return 'https://devcolo.verticalbridge.com';
  }

  navigateToColo(): void {
    window.location.href = this.getColoUrl();
  }

  // Call this to reload roles (e.g., after login)
  reloadRoles(): void {
    this.isRolesLoaded = false;
    this.initializeRoles();
  }

  // Force refresh roles from Auth0 (bypasses cache)
  // Returns a promise that resolves when refresh is complete
  // Includes debounce to prevent rapid refresh token usage (which causes 403 with token rotation)
  refreshRolesFromAuth0(force = false): Promise<AppRoles | null> {
    // If already refreshing, return the existing promise to avoid concurrent refresh token usage
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check debounce - prevent rapid refresh across page loads
    const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
    const now = Date.now();
    if (!force && lastRefresh) {
      const timeSinceLastRefresh = now - parseInt(lastRefresh, 10);
      if (timeSinceLastRefresh < REFRESH_DEBOUNCE_MS) {
        // Just load from existing token instead
        this.initializeRoles();
        return Promise.resolve(this.appRoles$.value);
      }
    }

    this._isRefreshing = true;
    this.isRolesLoaded = false;

    this.refreshPromise = new Promise<AppRoles | null>((resolve) => {
      this.auth0.getAccessTokenSilently({ cacheMode: 'off' }).pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (token) => {
          // Store the fresh token and update last refresh time
          localStorage.setItem('authToken', token);
          localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());

          const roles = this.decodeRolesFromToken(token);
          this._isRefreshing = false;
          this.refreshPromise = null;

          if (roles) {
            this.appRoles$.next(roles);
            this.isRolesLoaded = true;
            resolve(roles);
          } else {
            // Emit existing roles so UI stops loading
            this.appRoles$.next(this.appRoles$.value);
            this.isRolesLoaded = true;
            resolve(this.appRoles$.value);
          }
        },
        error: () => {
          // Don't clear token on error - just use existing roles
          this._isRefreshing = false;
          this.refreshPromise = null;
          this.initializeRoles();
          resolve(null);
        }
      });
    });

    return this.refreshPromise;
  }

  /**
   * Partner Portal's check on its own, merged into `getRoles()` as soon as PP
   * answers. The PP auto-redirect waits on this result, so it must not wait
   * on the slowest of the other apps' checks as well.
   */
  refreshPartnerPortalFromBackend(userEmail: string): Observable<AppAccess> {
    return this.checkPartnerPortal(encodeURIComponent(userEmail)).pipe(
      tap(partnerPortal => this.appRoles$.next({ ...this.appRoles$.value, partnerPortal }))
    );
  }

  // PP answers 200 once a request row exists, before any admin has assigned
  // a role. Only `approved` grants access; a 200 without it is a pending
  // request, which must not light the tile or trigger the auto-redirect.
  private checkPartnerPortal(encodedEmail: string): Observable<AppAccess> {
    return this.http.get<PartnerPortalAccessStatusResponse | null>(
      `${environment.partnerPortalApiUrl}/AccessRequest/status?userEmail=${encodedEmail}`
    ).pipe(
      timeout(ROLE_CHECK_TIMEOUT_MS),
      map((response): AppAccess => {
        const isApproved = response?.approved === true;
        return {
          hasAccess: isApproved,
          roles: [],
          userId: response?.userId ?? undefined,
          status: isApproved ? undefined : PartnerPortalAccessStatus.Pending,
          statusUnknown: false
        };
      }),
      // A 404 is an answer: PP has never seen the address. Anything else
      // (timeout, 5xx, CORS) left the question unanswered. That must not read
      // as "no access": this result decides the redirect and the tile, and a
      // Request Access offer would re-send an approved user's request.
      catchError((err: unknown) => {
        const isAnswered = err instanceof HttpErrorResponse && err.status === 404;
        return of<AppAccess>({ hasAccess: false, roles: [], statusUnknown: !isAnswered });
      })
    );
  }

  // Refresh roles directly from backend APIs (bypasses Auth0 token)
  // This calls the same endpoints that the Auth0 action uses
  // Use this after requesting access to immediately see the updated roles.
  // Pass isPartnerPortalIncluded: false when the caller runs
  // refreshPartnerPortalFromBackend itself; the merge then keeps whatever PP
  // value is current.
  refreshRolesFromBackend(
    userEmail: string,
    { isPartnerPortalIncluded = true }: { isPartnerPortalIncluded?: boolean } = {}
  ): Observable<AppRoles> {
    this._isRefreshing = true;

    const encodedEmail = encodeURIComponent(userEmail);

    // Call both APIs in parallel (same as Auth0 action)
    const coloCheck$ = this.http.get<{ roles?: string[]; userId?: string } | null>(
      `${environment.onlineColoApiUrl}/user/GetUser?userEmail=${encodedEmail}`
    ).pipe(
      timeout(ROLE_CHECK_TIMEOUT_MS),
      map(response => {
        // Only grant access if response has data (204 with empty body means no access)
        if (response) {
          return {
            hasAccess: true,
            roles: response.roles || [],
            userId: response.userId
          };
        }
        return { hasAccess: false, roles: [] as string[], userId: undefined as string | undefined };
      }),
      catchError(() => {
        return of({ hasAccess: false, roles: [] as string[], userId: undefined as string | undefined });
      })
    );

    const partnerPortalCheck$: Observable<AppAccess | null> = isPartnerPortalIncluded
      ? this.checkPartnerPortal(encodedEmail)
      : of(null);

    // Vertical Intel is not deployed in production yet, so the endpoint is
    // unreachable there and the request hangs until the browser gives up —
    // which (via forkJoin) would keep every tile disabled for seconds. Skip
    // the call outright in prod and report no access. In dev/test the call
    // runs, capped by the shared timeout as a backstop.
    const verticalIntelCheck$ = environment.production
      ? of({ hasAccess: false, roles: [] as string[], userId: undefined as string | undefined, status: undefined as string | undefined })
      : this.http.get<{ roles?: string[]; userId?: string; hasAccess?: boolean; status?: string } | null>(
      `${environment.verticalIntelApiUrl}/user/GetUser?userEmail=${encodedEmail}`
    ).pipe(
      timeout(ROLE_CHECK_TIMEOUT_MS),
      map(response => {
        if (response) {
          // New contract returns hasAccess/status explicitly; a revoked user is 200 with hasAccess=false.
          // Fall back to true when hasAccess is absent so pre-rollout responses keep working.
          const hasAccess = response.hasAccess ?? true;
          return {
            hasAccess,
            roles: response.roles || [],
            userId: response.userId,
            status: response.status
          };
        }
        return { hasAccess: false, roles: [] as string[], userId: undefined as string | undefined, status: undefined as string | undefined };
      }),
      catchError(() => {
        return of({ hasAccess: false, roles: [] as string[], userId: undefined as string | undefined, status: undefined as string | undefined });
      })
    );

    // Vertical Works answers hasAccess in the body, not through the status
    // code: 404 means "this address is entirely unknown", and a known user
    // without access is a 200 carrying hasAccess: false. So, as with the
    // Partner Portal check above, a 200 alone must not grant the tile.
    // Skipped entirely where Vertical Works isn't deployed (prod, where both
    // URLs are empty) rather than firing at the Portal's own origin.
    const verticalWorksCheck$ = isVerticalWorksConfigured()
      ? this.http.get<VerticalWorksAccessResponse | null>(
          `${environment.verticalWorksApiUrl}/access/status?userEmail=${encodedEmail}`
        ).pipe(
          timeout(ROLE_CHECK_TIMEOUT_MS),
          map(response => ({
            hasAccess: response?.hasAccess ?? false,
            roles: [] as string[],
            userId: response?.userId != null ? String(response.userId) : undefined,
            status: response?.status as string | undefined,
            statusUnknown: false
          })),
          // A 404 is an answer, not a failure: Vertical Works 404s only for an
          // address it has never seen, and that address is exactly who Request
          // Access is for. Everything else — a refused connection, a CORS
          // block, 5xx, the shared 20/min rate limit on /api/access, this
          // pipe's own timeout — means the question went unanswered, which is
          // a different tile.
          catchError((err: unknown) => {
            const answered = err instanceof HttpErrorResponse && err.status === 404;
            return of({
              hasAccess: false,
              roles: [] as string[],
              userId: undefined as string | undefined,
              status: undefined as string | undefined,
              statusUnknown: !answered
            });
          })
        )
      // Not configured is not unknown: both URLs empty means the tile is
      // hidden outright, so nothing reads this.
      : of({ hasAccess: false, roles: [] as string[], userId: undefined as string | undefined, status: undefined as string | undefined, statusUnknown: false });

    return forkJoin([coloCheck$, partnerPortalCheck$, verticalIntelCheck$, verticalWorksCheck$]).pipe(
      map(([coloAccess, partnerPortalAccess, verticalIntelAccess, verticalWorksAccess]) => {
        // Get current roles and merge with backend results
        const currentRoles = this.appRoles$.value;

        const updatedRoles: AppRoles = {
          ...currentRoles,
          colo: {
            hasAccess: coloAccess.hasAccess,
            roles: coloAccess.roles,
            userId: coloAccess.userId
          },
          siteLocator: {
            hasAccess: coloAccess.roles.some(role => role?.toLowerCase() === SITE_LOCATOR_ROLE),
            roles: coloAccess.roles.filter(role => role?.toLowerCase() === SITE_LOCATOR_ROLE),
            userId: coloAccess.userId
          },
          partnerPortal: partnerPortalAccess ?? currentRoles.partnerPortal,
          verticalIntel: {
            hasAccess: verticalIntelAccess.hasAccess,
            roles: verticalIntelAccess.roles,
            userId: verticalIntelAccess.userId,
            status: verticalIntelAccess.status
          },
          verticalWorks: {
            hasAccess: verticalWorksAccess.hasAccess,
            roles: verticalWorksAccess.roles,
            userId: verticalWorksAccess.userId,
            status: verticalWorksAccess.status,
            statusUnknown: verticalWorksAccess.statusUnknown
          }
        };

        this._isRefreshing = false;
        this.isRolesLoaded = true;
        this.appRoles$.next(updatedRoles);

        return updatedRoles;
      })
    );
  }
}
